import React, { createContext, useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import { lostSalesService } from "../utils/services/lost-sales.service";
import {
  getLostSalesErrorPresentation,
  isLostSalesErrorPayload,
} from "../utils/lostSalesErrors";
import {
  buildBulkLostSalesPayload,
  normalizeInventoryScopes,
  resolveActiveClientId,
  resolveAnalysisLevelFromAvailability,
  uniqueWarehouseIds,
  validateBulkSelection,
  sortBulkResults,
} from "../utils/lostSalesBulk";
import { getDefaultLostSalesDateRange } from "../utils/lostSalesPageDefaults";

const LostSalesContext = createContext();

const toId = (value) => {
  if (value == null) return null;
  if (typeof value === "object") {
    const id = value._id ?? value.id;
    return id != null ? String(id) : null;
  }
  return String(value);
};

export const DEFAULT_SEARCH_WINDOW_DAYS = 180;
const DEFAULT_DEMAND_WINDOW_DAYS = null;

export const LostSalesProvider = ({ children }) => {
  const [paramsDialogOpen, setParamsDialogOpen] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [selectedInventories, setSelectedInventories] = useState([]);
  const [bulkScopes, setBulkScopes] = useState([]);
  // null = unresolved; never treat unresolved as SKU-level for bulk.
  const [analysisLevel, setAnalysisLevel] = useState(null);
  const [selectionError, setSelectionError] = useState(null);
  const [startDate, setStartDate] = useState(
    () => getDefaultLostSalesDateRange().start_date,
  );
  const [endDate, setEndDate] = useState(
    () => getDefaultLostSalesDateRange().end_date,
  );
  const [searchWindowDays, setSearchWindowDays] = useState(
    DEFAULT_SEARCH_WINDOW_DAYS,
  );
  const [demandWindowDays, setDemandWindowDays] = useState(
    DEFAULT_DEMAND_WINDOW_DAYS,
  );
  const [dowAdjustmentEnabled, setDowAdjustmentEnabled] = useState(true);
  const [result, setResult] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [detailResult, setDetailResult] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bulkParamsLoading, setBulkParamsLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearSelectedRef = useRef(null);

  const resetParams = () => {
    const defaults = getDefaultLostSalesDateRange();
    setStartDate(defaults.start_date);
    setEndDate(defaults.end_date);
    setSearchWindowDays(DEFAULT_SEARCH_WINDOW_DAYS);
    setDemandWindowDays(DEFAULT_DEMAND_WINDOW_DAYS);
    setDowAdjustmentEnabled(true);
    setResult(null);
    setBulkResult(null);
    setDetailResult(null);
    setError(null);
    setSelectionError(null);
  };

  // Prefill the calculation parameters with the client's Lost Sales settings
  // (Client Config → Dashboard settings); the user can still change them.
  const applyClientSettings = (settings) => {
    const parameters = settings?.parameters;
    if (!parameters) return;
    setSearchWindowDays(parameters.search_window_days ?? DEFAULT_SEARCH_WINDOW_DAYS);
    setDemandWindowDays(parameters.demand_window_days ?? DEFAULT_DEMAND_WINDOW_DAYS);
    setDowAdjustmentEnabled(parameters.dow_adjustment_enabled !== false);
  };

  const openLostSalesParams = (inventory) => {
    setSelectedInventory(inventory);
    setSelectedInventories(inventory ? [inventory] : []);
    setBulkScopes([]);
    // Single-item UI does not display Mode; backend resolves grain.
    setAnalysisLevel(null);
    clearSelectedRef.current = null;
    resetParams();
    setParamsDialogOpen(true);
    const clientId = resolveActiveClientId();
    if (clientId) {
      lostSalesService
        .availability(clientId)
        .then((availability) => applyClientSettings(availability?.settings))
        .catch(() => {});
    }
  };

  const openLostSalesParamsBulk = async (inventories, callbacks = {}) => {
    resetParams();
    setSelectedInventory(null);
    setSelectedInventories(inventories || []);
    setBulkScopes([]);
    setAnalysisLevel(null);
    clearSelectedRef.current = callbacks.onClearSelected || null;

    const clientId = resolveActiveClientId();
    if (!clientId) {
      toast.error("Please select a client before running Lost Sales analysis.");
      return;
    }

    setBulkParamsLoading(true);
    try {
      const availability = await lostSalesService.availability(clientId);
      const resolved = resolveAnalysisLevelFromAvailability(availability);
      if (!resolved.ok) {
        toast.error(resolved.message);
        return;
      }

      applyClientSettings(availability?.settings);
      const level = resolved.analysisLevel;
      const warehouseIds = uniqueWarehouseIds(inventories);
      const scopes = normalizeInventoryScopes(inventories, level);
      setAnalysisLevel(level);
      setBulkScopes(scopes);

      const defaultDates = getDefaultLostSalesDateRange();
      const validation = validateBulkSelection({
        scopes,
        warehouseIds,
        startDate: defaultDates.start_date,
        endDate: defaultDates.end_date,
      });
      if (!validation.ok) {
        setSelectionError(validation.message);
      }

      setParamsDialogOpen(true);
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message ||
        "Unable to determine Lost Sales analysis mode. Please try again.";
      toast.error(message);
    } finally {
      setBulkParamsLoading(false);
    }
  };

  const isBulkMode =
    bulkScopes.length > 1 ||
    (bulkScopes.length === 1 && !selectedInventory) ||
    selectedInventories.length > 1;

  const submitLostSales = async () => {
    if (searchWindowDays !== null && searchWindowDays !== undefined) {
      if (!Number.isInteger(searchWindowDays) || searchWindowDays <= 0) {
        return;
      }
    }

    const resolvedSearchWindow =
      searchWindowDays == null
        ? DEFAULT_SEARCH_WINDOW_DAYS
        : searchWindowDays;

    // Single-item path (existing inventory row action / single selection)
    if (!isBulkMode && selectedInventory) {
      setSubmitting(true);
      setError(null);
      try {
        const analysis = await lostSalesService.analyze({
          product: toId(selectedInventory.product),
          warehouse_id: toId(selectedInventory.warehouse),
          client: toId(selectedInventory.client) || resolveActiveClientId(),
          sku: selectedInventory.sku,
          start_date: startDate,
          end_date: endDate,
          search_window_days: resolvedSearchWindow,
          demand_window_days: demandWindowDays,
          dow_adjustment_enabled: dowAdjustmentEnabled,
        });
        if (isLostSalesErrorPayload(analysis)) {
          setError(
            getLostSalesErrorPresentation(analysis, {
              warehouse: selectedInventory?.warehouse,
              startDate,
              endDate,
            }),
          );
          return;
        }
        setResult(analysis);
        setBulkResult(null);
        setParamsDialogOpen(false);
        setResultDialogOpen(true);
      } catch (requestError) {
        setError(
          getLostSalesErrorPresentation(requestError, {
            warehouse: selectedInventory?.warehouse,
            startDate,
            endDate,
          }),
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Bulk path — grain must be confirmed before run.
    if (analysisLevel !== "product" && analysisLevel !== "sku") {
      setError({
        title: "Analysis mode unavailable",
        body: "Lost Sales analysis mode could not be determined. Close this dialog and try again.",
      });
      return;
    }

    const warehouseIds = uniqueWarehouseIds(selectedInventories);
    const scopes =
      bulkScopes.length > 0
        ? bulkScopes
        : normalizeInventoryScopes(selectedInventories, analysisLevel);
    const validation = validateBulkSelection({
      scopes,
      warehouseIds,
      startDate,
      endDate,
    });
    if (!validation.ok) {
      setSelectionError(validation.message);
      return;
    }

    setSubmitting(true);
    setError(null);
    setSelectionError(null);
    try {
      const payload = buildBulkLostSalesPayload({
        scopes,
        warehouseId: warehouseIds[0],
        clientId: resolveActiveClientId() || scopes[0]?.client,
        startDate,
        endDate,
        searchWindowDays: resolvedSearchWindow,
        demandWindowDays,
        dowAdjustmentEnabled,
        includeBreakdown: "monthly",
      });
      const analysis = await lostSalesService.analyzeBulk(payload);
      if (isLostSalesErrorPayload(analysis)) {
        setError(
          getLostSalesErrorPresentation(analysis, {
            warehouse: selectedInventories[0]?.warehouse,
            startDate,
            endDate,
          }),
        );
        return;
      }
      // Same ranking as the All Products / All SKUs page so bulk results are
      // ordered consistently wherever they are rendered.
      setBulkResult({
        ...analysis,
        results: sortBulkResults(analysis.results),
      });
      setResult(null);
      setDetailResult(null);
      setParamsDialogOpen(false);
      setResultDialogOpen(true);
      clearSelectedRef.current?.();
    } catch (requestError) {
      setError(
        getLostSalesErrorPresentation(requestError, {
          warehouse: selectedInventories[0]?.warehouse,
          startDate,
          endDate,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openBulkDetail = async (row) => {
    if (!row || row.status !== "complete" || !bulkResult?.shared) return;
    setDetailLoading(true);
    setDetailResult(null);
    try {
      const shared = bulkResult.shared;
      const analysis = await lostSalesService.analyze({
        product: row.scope.product,
        warehouse_id: shared.warehouse_id,
        client: shared.client,
        sku: row.scope.sku || undefined,
        start_date: shared.start_date,
        end_date: shared.end_date,
        search_window_days: shared.search_window_days,
        demand_window_days: shared.demand_window_days,
        dow_adjustment_enabled: shared.dow_adjustment_enabled,
      });
      if (isLostSalesErrorPayload(analysis)) {
        setError(
          getLostSalesErrorPresentation(analysis, {
            startDate: shared.start_date,
            endDate: shared.end_date,
          }),
        );
        return;
      }
      setDetailResult(analysis);
    } catch (requestError) {
      setError(getLostSalesErrorPresentation(requestError));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailResult(null);
  };

  return (
    <LostSalesContext.Provider
      value={{
        paramsDialogOpen,
        setParamsDialogOpen,
        resultDialogOpen,
        setResultDialogOpen,
        selectedInventory,
        selectedInventories,
        bulkScopes,
        analysisLevel,
        selectionError,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        searchWindowDays,
        setSearchWindowDays,
        demandWindowDays,
        setDemandWindowDays,
        dowAdjustmentEnabled,
        setDowAdjustmentEnabled,
        result,
        bulkResult,
        detailResult,
        detailLoading,
        error,
        clearError: () => setError(null),
        submitting,
        bulkParamsLoading,
        isBulkMode,
        openLostSalesParams,
        openLostSalesParamsBulk,
        submitLostSales,
        openBulkDetail,
        closeDetail,
      }}
    >
      {children}
    </LostSalesContext.Provider>
  );
};

export const useLostSales = () => useContext(LostSalesContext);
